/**
 * Shared "mark as reviewed" behavior for feed cards.
 *
 * On the decluttered Active view the card plays an exit animation, then the
 * caller removes it from the list so siblings can close the gap. On All /
 * Reviewed the card stays put — the caller just stamps `is_reviewed`.
 *
 * Booking activity supplies its own review filter via
 * {@link ReviewStatusOverrideProvider} so the global feed's Active default
 * cannot animate rows out of that timeline.
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useFeedOptional } from "@/context/FeedContext";
import type { ReviewStatus } from "@/types/feed";

/** Matches `.feed-row-slot` / `.rail-notification-pill--review-exit` duration. */
export const FEED_ROW_REMOVE_MS = 420;

const ReviewStatusOverrideContext = createContext<ReviewStatus | undefined>(
  undefined,
);

/** Local review filter for a feed list that is not the shared FeedContext. */
export function ReviewStatusOverrideProvider({
  value,
  children,
}: {
  value: ReviewStatus;
  children: ReactNode;
}) {
  return createElement(
    ReviewStatusOverrideContext.Provider,
    { value },
    children,
  );
}

export function shouldAnimateFeedReviewExit(
  reviewStatus: ReviewStatus | null | undefined,
): boolean {
  return reviewStatus !== "all" && reviewStatus !== "reviewed";
}

function reviewExitDurationMs(): number {
  if (typeof window === "undefined") return FEED_ROW_REMOVE_MS;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? 0
    : FEED_ROW_REMOVE_MS;
}

export function useFeedRowReview() {
  const feed = useFeedOptional();
  const override = useContext(ReviewStatusOverrideContext);
  const [removing, setRemoving] = useState(false);
  const timerRef = useRef<number | null>(null);
  const shouldAnimateExit = shouldAnimateFeedReviewExit(
    override ?? feed?.filters.reviewStatus,
  );

  useEffect(() => {
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const startReview = useCallback(
    (commit: () => void | Promise<void>) => {
      if (removing) return;
      const run = async () => {
        timerRef.current = null;
        try {
          await commit();
        } catch {
          setRemoving(false);
        }
      };
      const duration = reviewExitDurationMs();
      if (shouldAnimateExit && duration > 0) {
        setRemoving(true);
        timerRef.current = window.setTimeout(() => {
          void run();
        }, duration);
        return;
      }
      void run();
    },
    [removing, shouldAnimateExit],
  );

  return { removing, startReview, shouldAnimateExit };
}
